import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Copy, ExternalLink, Mail } from "lucide-react";
import { toast } from "sonner";
import { SOCIALS } from "@/lib/site";
import { FadeUp, MaskLines } from "@/components/Reveal";

export default function Contact() {
  const [copiedId, setCopiedId] = useState(null);

  const copyHandle = async (s) => {
    try {
      await navigator.clipboard.writeText(s.handle);
      setCopiedId(s.id);
      toast.success(`Copied ${s.handle} — see you there`);
      setTimeout(() => setCopiedId(null), 1600);
    } catch {
      toast.error(`Couldn't copy — the handle is ${s.handle}`);
    }
  };

  return (
    <div className="pt-16">
      <section className="hero-glow relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 py-20 text-center sm:px-6 lg:px-8 lg:py-28">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-husko-cyan">
            Dens & direct lines
          </p>
          <h1 className="mt-5 font-display text-4xl font-extrabold uppercase leading-[1.02] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
            <MaskLines baseDelay={0.1} lines={["Say", <span key="a" className="text-gradient-husky">hi.</span>]} />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.7 }}
            className="mx-auto mt-6 max-w-lg text-base text-slate-300 sm:text-lg"
          >
            Six portals, one husko. Pick your platform — or send a carrier pigeon, Husko
            will chase it either way.
          </motion.p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8 lg:pb-32">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SOCIALS.map((s, i) => {
            const Icon = s.icon;
            const copied = copiedId === s.id;
            return (
              <FadeUp key={s.id} delay={(i % 3) * 0.08}>
                <motion.div
                  whileHover={{ y: -6 }}
                  transition={{ type: "spring", stiffness: 300, damping: 22 }}
                  className="group flex h-full flex-col rounded-3xl border border-white/10 bg-den p-7 transition-colors hover:border-husko-cyan/50"
                  data-testid={`social-card-${s.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className="rounded-2xl p-3 transition-transform group-hover:scale-110"
                      style={{ color: s.color, background: `${s.color}1a` }}
                    >
                      <Icon size={26} />
                    </span>
                    <span
                      className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] opacity-0 transition-opacity group-hover:opacity-100"
                      style={{ color: s.color }}
                    >
                      Open den
                    </span>
                  </div>
                  <h2 className="mt-5 font-display text-xl font-extrabold text-slate-50">{s.name}</h2>
                  <p className="mt-1 font-mono text-xs text-husko-cyan">{s.handle}</p>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">{s.blurb}</p>
                  <div className="mt-6 flex items-center gap-2">
                    <a
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`social-link-${s.id}`}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-white/15 px-4 py-2.5 font-display text-xs font-extrabold uppercase tracking-wide text-slate-100 transition-colors hover:border-husko-cyan hover:text-husko-cyan"
                    >
                      Visit <ExternalLink size={13} />
                    </a>
                    <button
                      data-testid={`social-copy-${s.id}`}
                      onClick={() => copyHandle(s)}
                      aria-label={`Copy ${s.name} handle`}
                      className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 font-display text-xs font-extrabold uppercase tracking-wide text-slate-300 transition-colors hover:border-husko-magenta hover:text-husko-magenta"
                    >
                      {copied ? <Check size={13} className="text-husko-cyan" /> : <Copy size={13} />}
                      {copied ? "Copied" : "Copy"}
                    </button>
                  </div>
                </motion.div>
              </FadeUp>
            );
          })}
        </div>

        <FadeUp delay={0.1}>
          <div
            className="mt-12 flex flex-col items-center justify-between gap-6 rounded-3xl border border-white/10 bg-gradient-to-br from-husko-cyan/10 via-den to-husko-magenta/10 p-10 md:flex-row"
            data-testid="contact-business-card"
          >
            <div className="flex items-center gap-5">
              <span className="rounded-2xl bg-husko-yellow/15 p-4 text-husko-yellow">
                <Mail size={28} />
              </span>
              <div>
                <h2 className="font-display text-xl font-extrabold text-slate-50">Contact info</h2>
                <p className="mt-1 text-sm text-slate-400">
                  Email me directly — a hello, a collab or anything in between.
                </p>
              </div>
            </div>
            <a
              href="mailto:business@thehuskobiscuit.com"
              data-testid="contact-email-link"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-husko-cyan to-husko-purple px-7 py-3.5 font-display text-sm font-extrabold uppercase tracking-wide text-void transition-transform hover:scale-[1.03]"
            >
              business@thehuskobiscuit.com
            </a>
          </div>
        </FadeUp>
      </section>
    </div>
  );
}
